import { Text } from '@viapeople/components'
import { useMemo, useState } from 'react'

export interface FeedbackResponse {
	id: string
	participantName?: string
	participantEmail?: string
	score: number | null
	feedback: string | null
	submittedAt: Date | null
	category: string | null
}

interface EnpsFeedbackListProps {
	responses: FeedbackResponse[]
	showParticipantInfo?: boolean
	initialPageSize?: number
	pageSize?: number
}

type CategoryFilter = 'all' | 'promoter' | 'passive' | 'detractor'
type SortOrder = 'desc' | 'asc' | 'none'

export const EnpsFeedbackList = ({
	responses,
	showParticipantInfo = false,
	initialPageSize = 10,
	pageSize = 20,
}: EnpsFeedbackListProps) => {
	const [searchQuery, setSearchQuery] = useState('')
	const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
	const [sortOrder, setSortOrder] = useState<SortOrder>('none')
	const [displayCount, setDisplayCount] = useState(initialPageSize)
	const [isExpanded, setIsExpanded] = useState(false)

	// Filter and sort responses
	const filteredAndSortedResponses = useMemo(() => {
		let filtered = [...responses]

		// Apply category filter
		if (categoryFilter !== 'all') {
			filtered = filtered.filter((response) => response.category === categoryFilter)
		}

		// Apply search filter
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase()
			filtered = filtered.filter(
				(response) =>
					response.feedback?.toLowerCase().includes(query) ||
					response.participantName?.toLowerCase().includes(query) ||
					response.participantEmail?.toLowerCase().includes(query),
			)
		}

		// Apply sorting
		if (sortOrder !== 'none') {
			filtered.sort((a, b) => {
				const scoreA = a.score ?? -1
				const scoreB = b.score ?? -1
				return sortOrder === 'desc' ? scoreB - scoreA : scoreA - scoreB
			})
		}

		return filtered
	}, [responses, categoryFilter, searchQuery, sortOrder])

	const displayedResponses = isExpanded ? filteredAndSortedResponses : filteredAndSortedResponses.slice(0, displayCount)

	const hasMore = filteredAndSortedResponses.length > displayCount

	const getCategoryColor = (category: string | null) => {
		switch (category) {
			case 'promoter':
				return 'text-green-600'
			case 'passive':
				return 'text-yellow-600'
			case 'detractor':
				return 'text-red-600'
			default:
				return 'text-gray-600'
		}
	}

	const getCategoryLabel = (category: string | null) => {
		if (!category) return 'Pending'
		return category.charAt(0).toUpperCase() + category.slice(1)
	}

	return (
		<div className='space-y-4'>
			{/* Search and Filter Controls */}
			<div className='space-y-3'>
				{/* Search Bar */}
				<div>
					<input
						type='text'
						placeholder='Search feedback, names, or emails...'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className='w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
					/>
				</div>

				{/* Filter and Sort Controls */}
				<div className='flex flex-wrap items-center gap-3'>
					{/* Category Filter */}
					<div className='flex items-center gap-2'>
						<Text variant='xsmall' className='text-gray-600'>
							Filter:
						</Text>
						<div className='flex rounded-lg border border-gray-200 overflow-hidden'>
							<button
								type='button'
								onClick={() => setCategoryFilter('all')}
								className={`px-3 py-1 text-xs font-medium transition-colors ${
									categoryFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
								}`}>
								All
							</button>
							<button
								type='button'
								onClick={() => setCategoryFilter('promoter')}
								className={`px-3 py-1 text-xs font-medium transition-colors border-l ${
									categoryFilter === 'promoter' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
								}`}>
								Promoters
							</button>
							<button
								type='button'
								onClick={() => setCategoryFilter('passive')}
								className={`px-3 py-1 text-xs font-medium transition-colors border-l ${
									categoryFilter === 'passive' ? 'bg-yellow-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
								}`}>
								Passives
							</button>
							<button
								type='button'
								onClick={() => setCategoryFilter('detractor')}
								className={`px-3 py-1 text-xs font-medium transition-colors border-l ${
									categoryFilter === 'detractor' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
								}`}>
								Detractors
							</button>
						</div>
					</div>

					{/* Sort Controls */}
					<div className='flex items-center gap-2'>
						<Text variant='xsmall' className='text-gray-600'>
							Sort by Score:
						</Text>
						<div className='flex rounded-lg border border-gray-200 overflow-hidden'>
							<button
								type='button'
								onClick={() => setSortOrder('none')}
								className={`px-3 py-1 text-xs font-medium transition-colors ${
									sortOrder === 'none' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
								}`}>
								None
							</button>
							<button
								type='button'
								onClick={() => setSortOrder('desc')}
								className={`px-3 py-1 text-xs font-medium transition-colors border-l ${
									sortOrder === 'desc' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
								}`}>
								↓ High to Low
							</button>
							<button
								type='button'
								onClick={() => setSortOrder('asc')}
								className={`px-3 py-1 text-xs font-medium transition-colors border-l ${
									sortOrder === 'asc' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
								}`}>
								↑ Low to High
							</button>
						</div>
					</div>

					{/* Results Count */}
					<Text variant='xsmall' className='text-gray-500 ml-auto'>
						Showing {displayedResponses.length} of {filteredAndSortedResponses.length} responses
					</Text>
				</div>
			</div>

			{/* Response List */}
			{filteredAndSortedResponses.length === 0 ? (
				<div className='bg-gray-50 border border-gray-200 rounded-lg p-6 text-center'>
					<Text variant='small' className='text-gray-600'>
						No responses match your filters
					</Text>
				</div>
			) : (
				<>
					<div className='space-y-3 max-h-[600px] overflow-y-auto'>
						{displayedResponses.map((response) => (
							<div key={response.id} className='bg-gray-50 rounded-lg p-3 text-sm'>
								<div className='flex justify-between items-start'>
									<div className='flex items-center gap-3'>
										<Text variant='xsmall' className={getCategoryColor(response.category)}>
											Score: {response.score !== null ? response.score : 'Pending'}
										</Text>
										<Text variant='xsmall' className={`${getCategoryColor(response.category)} font-medium`}>
											{getCategoryLabel(response.category)}
										</Text>
										{showParticipantInfo && response.participantName && (
											<Text variant='xsmall' className='text-gray-500'>
												• {response.participantName}
											</Text>
										)}
									</div>
									<Text variant='xsmall' className='text-gray-500'>
										{response.submittedAt ? new Date(response.submittedAt).toLocaleDateString() : 'Not submitted'}
									</Text>
								</div>
								{response.feedback && (
									<Text variant='xsmall' className='text-gray-700 mt-2'>
										"{response.feedback}"
									</Text>
								)}
							</div>
						))}
					</div>

					{/* Show More / Collapse Button */}
					{!isExpanded && hasMore && (
						<button
							type='button'
							onClick={() => {
								setDisplayCount((prev) => prev + pageSize)
								if (displayCount + pageSize >= filteredAndSortedResponses.length) {
									setIsExpanded(true)
								}
							}}
							className='w-full px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors'>
							Show More (+{Math.min(pageSize, filteredAndSortedResponses.length - displayCount)} more responses)
						</button>
					)}

					{isExpanded && filteredAndSortedResponses.length > initialPageSize && (
						<button
							type='button'
							onClick={() => {
								setIsExpanded(false)
								setDisplayCount(initialPageSize)
							}}
							className='w-full px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors'>
							Show Less
						</button>
					)}
				</>
			)}
		</div>
	)
}
